type StreamingFetchOptions = {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    body?: FormData | string | Blob | ArrayBuffer | URLSearchParams;
    onProgress?: (percentage: number) => void;
};

type StreamingFetchResult<T> = {
    data: T | null;
    error: { message: string; errors: unknown[] } | null;
};

export const streamingFetch = async <T>(options: StreamingFetchOptions): Promise<StreamingFetchResult<T>> => {
    const { url, method = 'POST', headers, body, onProgress } = options;

    const response = await fetch(url, { method, headers, body });

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let fullText = '';

    if (reader) {
        let done = false;
        while (!done) {
            // eslint-disable-next-line no-await-in-loop
            const result = await reader.read();
            done = result.done;
            if (!done && result.value) {
                const chunk = decoder.decode(result.value, { stream: true });
                fullText += chunk;
                if (onProgress) {
                    chunk
                        .split('\n')
                        .map((line) => line.trim())
                        .filter((line) => line.length > 0)
                        .forEach((line) => {
                            try {
                                const json = JSON.parse(line);
                                if (typeof json.percentage === 'number') {
                                    onProgress(json.percentage);
                                }
                            } catch {
                                // Skip non-JSON lines
                            }
                        });
                }
            }
        }
    } else {
        fullText = await response.text();
    }

    if (!response.ok) {
        if (!fullText) {
            return { data: null, error: { message: `HTTP ${response.status}: ${response.statusText}`, errors: [] } };
        }
        try {
            const errorJson = JSON.parse(fullText);
            return {
                data: null,
                error: { message: errorJson.message || errorJson.error || `HTTP ${response.status}`, errors: [] },
            };
        } catch (e) {
            if (e instanceof SyntaxError) {
                return {
                    data: null,
                    error: {
                        message: fullText.length < 200 ? fullText : `HTTP ${response.status}: ${response.statusText}`,
                        errors: [],
                    },
                };
            }
            throw e;
        }
    }

    // Parse line by line to find the final result (API streams progress then final JSON)
    let parsedResult: StreamingFetchResult<T> | null = null;
    fullText
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .forEach((line) => {
            if (parsedResult) return;
            try {
                const json = JSON.parse(line);
                if (typeof json.status === 'number') {
                    if (json.status >= 400) {
                        parsedResult = {
                            data: null,
                            error: {
                                message: json.message || json.code || 'Request failed',
                                errors: json.extraData?.errors ?? [],
                            },
                        };
                    } else {
                        parsedResult = { data: json.data as T, error: null };
                    }
                }
            } catch {
                // Skip non-JSON lines
            }
        });
    if (parsedResult) return parsedResult;

    // Fallback: progress and result may be concatenated without newlines.
    // Find the last JSON object containing "status" in the response.
    const statusIdx = fullText.lastIndexOf('{"status"');
    if (statusIdx !== -1) {
        try {
            const json = JSON.parse(fullText.slice(statusIdx));
            if (typeof json.status === 'number') {
                if (json.status >= 400) {
                    return {
                        data: null,
                        error: {
                            message: json.message || json.code || 'Request failed',
                            errors: json.extraData?.errors ?? [],
                        },
                    };
                }
                return { data: json.data as T, error: null };
            }
        } catch {
            // Could not parse fallback
        }
    }

    return { data: null, error: { message: 'Could not parse response', errors: [] } };
};
